import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Handshake, Building2 } from "lucide-react";
import { createFPOInterest } from "@/lib/api";
import { JALNA_TALUKAS, JALNA_VILLAGES, MAHARASHTRA_DISTRICTS } from "@/data/jalnaVillages";

const HERO = "https://images.unsplash.com/photo-1509660933844-6910e12765a0?auto=format&fit=crop&w=1600&q=80";

export default function FPOInterest() {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({
    fpo_name: "", registration_no: "", contact_person: "", phone: "", email: "",
    district: "Jalna", state: "Maharashtra", taluka: "", village: "",
    member_count: "", annual_supply_capacity: "",
    commodities: "", interest_topic: "topic_procurement", notes: "", consent: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const cleanPhone = form.phone.replace(/\D/g, "").slice(-10);
    if (cleanPhone.length !== 10) { toast.error(t("farmer_interest.invalid_phone")); return; }
    if (!form.consent) { toast.error(t("farmer_interest.consent_required")); return; }
    if (!form.fpo_name || !form.contact_person) { toast.error(t("toast.fill_required")); return; }
    setBusy(true);
    try {
      await createFPOInterest({
        fpo_name: form.fpo_name,
        registration_no: form.registration_no || null,
        contact_person: form.contact_person,
        phone: cleanPhone,
        email: form.email || null,
        district: form.district,
        state: form.state,
        taluka: form.taluka || null,
        village: form.village || null,
        member_count: form.member_count ? parseInt(form.member_count) : null,
        annual_supply_capacity: form.annual_supply_capacity || null,
        commodities: form.commodities ? form.commodities.split(",").map((s) => s.trim()).filter(Boolean) : [],
        interest_topic: form.interest_topic,
        notes: form.notes || null,
        consent: true,
        preferred_language: i18n.language || "en",
      });
      setDone(true);
      toast.success(t("fpo_interest.success_title"));
    } catch (err) {
      toast.error(err?.response?.data?.detail?.error || t("toast.generic_error"));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="page-fpo-interest">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0"><img src={HERO} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 tr-hero-overlay" /></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 text-white">
          <div className="tr-eyebrow !text-[var(--tr-citrus-yellow)] before:!bg-[var(--tr-citrus-yellow)]">{t("fpo_interest.eyebrow")}</div>
          <h1 className="mt-4 font-display font-extrabold text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tight max-w-3xl">
            {t("fpo_interest.title_a")} <span className="text-[var(--tr-citrus-yellow)]">{t("fpo_interest.title_b")}</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/85">{t("fpo_interest.body")}</p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
        {done ? (
          <div data-testid="fpo-interest-success" className="tr-card p-12 text-center">
            <CheckCircle2 className="w-16 h-16 text-[var(--tr-lime)] mx-auto" />
            <h3 className="font-display font-extrabold text-3xl mt-4 text-[var(--tr-deep-green)]">{t("fpo_interest.success_title")}</h3>
            <p className="mt-4 text-[var(--tr-muted)] max-w-xl mx-auto">{t("fpo_interest.success_body")}</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="fpo-interest-form" className="tr-card p-8 lg:p-10 space-y-6">
            <div className="flex items-center gap-3">
              <Building2 className="w-6 h-6 text-[var(--tr-deep-green)]" />
              <h2 className="font-display font-extrabold text-2xl text-[var(--tr-deep-green)]">{t("fpo_interest.eyebrow")}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Fld label={`${t("fpo_interest.f_fpo_name")}*`}><input required data-testid="fpo-name" className="tr-input" value={form.fpo_name} onChange={set("fpo_name")} /></Fld>
              <Fld label={t("fpo_interest.f_registration_no")}><input data-testid="fpo-reg-no" className="tr-input" value={form.registration_no} onChange={set("registration_no")} /></Fld>
              <Fld label={`${t("fpo_interest.f_contact_person")}*`}><input required data-testid="fpo-contact" className="tr-input" value={form.contact_person} onChange={set("contact_person")} /></Fld>
              <Fld label={`${t("fpo_interest.f_phone")}*`}><input required inputMode="tel" data-testid="fpo-phone" className="tr-input" value={form.phone} onChange={set("phone")} /></Fld>
              <Fld label={t("fpo_interest.f_email")}><input type="email" data-testid="fpo-email" className="tr-input" value={form.email} onChange={set("email")} /></Fld>
              <Fld label={t("fpo_interest.f_member_count")}><input type="number" data-testid="fpo-members" className="tr-input" value={form.member_count} onChange={set("member_count")} /></Fld>
              <Fld label={t("fpo_interest.f_district")}>
                <select data-testid="fpo-district" className="tr-input" value={form.district} onChange={(e) => setForm((f) => ({ ...f, district: e.target.value, taluka: "", village: "" }))}>
                  {MAHARASHTRA_DISTRICTS.map((d) => <option key={d}>{d}</option>)}
                </select>
              </Fld>
              <Fld label="Taluka">
                {form.district === "Jalna" ? (
                  <select data-testid="fpo-taluka" className="tr-input" value={form.taluka} onChange={(e) => setForm((f) => ({ ...f, taluka: e.target.value, village: "" }))}>
                    <option value="">— select —</option>
                    {JALNA_TALUKAS.map((tk) => <option key={tk}>{tk}</option>)}
                  </select>
                ) : (
                  <input data-testid="fpo-taluka" className="tr-input" value={form.taluka} onChange={set("taluka")} />
                )}
              </Fld>
              <Fld label="Village">
                {form.district === "Jalna" && form.taluka && JALNA_VILLAGES[form.taluka] ? (
                  <select data-testid="fpo-village" className="tr-input" value={form.village} onChange={set("village")}>
                    <option value="">— select —</option>
                    {JALNA_VILLAGES[form.taluka].map((v) => <option key={v}>{v}</option>)}
                  </select>
                ) : (
                  <input data-testid="fpo-village" className="tr-input" value={form.village} onChange={set("village")} />
                )}
              </Fld>
              <Fld label={t("fpo_interest.f_state")}><input data-testid="fpo-state" className="tr-input" value={form.state} onChange={set("state")} /></Fld>
              <Fld label="Annual supply capacity (tons)">
                <input data-testid="fpo-supply-capacity" className="tr-input" placeholder="e.g. 250 MT" value={form.annual_supply_capacity} onChange={set("annual_supply_capacity")} />
              </Fld>
              <Fld label={t("fpo_interest.f_commodities")} className="md:col-span-2">
                <input data-testid="fpo-commodities" className="tr-input" placeholder={t("fpo_interest.f_commodities_ph")} value={form.commodities} onChange={set("commodities")} />
              </Fld>
              <Fld label={t("fpo_interest.f_topic")} className="md:col-span-2">
                <select data-testid="fpo-topic" className="tr-input" value={form.interest_topic} onChange={set("interest_topic")}>
                  <option value="topic_procurement">{t("fpo_interest.topic_procurement")}</option>
                  <option value="topic_training">{t("fpo_interest.topic_training")}</option>
                  <option value="topic_credit">{t("fpo_interest.topic_credit")}</option>
                  <option value="topic_other">{t("fpo_interest.topic_other")}</option>
                </select>
              </Fld>
              <Fld label={t("fpo_interest.f_notes")} className="md:col-span-2"><textarea data-testid="fpo-notes" className="tr-input tr-textarea" value={form.notes} onChange={set("notes")} /></Fld>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" data-testid="fpo-consent" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} className="mt-1 w-5 h-5 accent-[var(--tr-deep-green)]" />
              <span className="text-sm text-[var(--tr-deep-green)] leading-relaxed">{t("fpo_interest.f_consent")}</span>
            </label>
            <button type="submit" disabled={busy} data-testid="fpo-submit" className="tr-pill-btn tr-btn-primary disabled:opacity-60">
              {busy ? t("common.submitting") : t("fpo_interest.f_submit")} <Handshake className="w-4 h-4" />
            </button>
          </form>
        )}
      </section>
    </div>
  );
}

function Fld({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="tr-label">{label}</span>
      {children}
    </label>
  );
}
